import { useMutation, useQueryClient } from '@tanstack/react-query';
import { familyService } from '../api/familyService';
import {
  UpdateFamilyRequest,
  UpdateAdultRequest,
  UpdateChildRequest,
  AddAdultRequest,
  AddChildRequest,
} from '../types/family.types';
import { RelationshipType } from '../types/enums';
import toast from 'react-hot-toast';

export const useFamilyMutations = (familyId: string) => {
  const queryClient = useQueryClient();

  const updateFamily = useMutation({
    mutationFn: (data: UpdateFamilyRequest) => familyService.updateMyFamily(familyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['my-families'] });
      toast.success('Family details updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update family details');
    },
  });

  const updateAdult = useMutation({
    mutationFn: ({ adultId, data }: { adultId: string; data: UpdateAdultRequest }) =>
      familyService.updateMyFamilyAdult(adultId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      toast.success('Adult profile updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update adult profile');
    },
  });

  const updateChild = useMutation({
    mutationFn: ({ childId, data }: { childId: string; data: UpdateChildRequest }) =>
      familyService.updateMyFamilyChild(childId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });

      if (response.photoConsentChangedToNo) {
        toast.success('Child profile updated. Photo consent change has been sent for review.', { duration: 5000 });
      } else {
        toast.success('Child profile updated successfully');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update child profile');
    },
  });

  const addAdult = useMutation({
    mutationFn: (data: AddAdultRequest) => familyService.addAdultToFamily(familyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['my-families'] });
      toast.success('Adult added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add adult');
    },
  });

  const addChild = useMutation({
    mutationFn: (data: AddChildRequest) => familyService.addChildToFamily(familyId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['my-families'] });
      toast.success('Child added successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add child');
    },
  });

  const deleteAdult = useMutation({
    mutationFn: (adultId: string) => familyService.deleteAdult(adultId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['my-families'] });
      toast.success('Adult removed from family');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove adult');
    },
  });

  const deleteChild = useMutation({
    mutationFn: (childId: string) => familyService.deleteChild(childId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      queryClient.invalidateQueries({ queryKey: ['my-families'] });
      toast.success('Child removed from family');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove child');
    },
  });

  const updateRelationship = useMutation({
    mutationFn: ({
      relationshipId,
      relationshipType,
    }: {
      relationshipId: string;
      relationshipType: RelationshipType;
    }) => familyService.updateRelationship(relationshipId, { relationshipType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family', familyId] });
      toast.success('Relationship updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update relationship');
    },
  });

  return {
    updateFamily,
    updateAdult,
    updateChild,
    updateRelationship,
    addAdult,
    addChild,
    deleteAdult,
    deleteChild,
    isUpdating:
      updateFamily.isPending || updateAdult.isPending || updateChild.isPending || updateRelationship.isPending,
    isAdding: addAdult.isPending || addChild.isPending,
    isDeleting: deleteAdult.isPending || deleteChild.isPending,
  };
};
